/**
 * Dashboard Page — types, constants, and pure helper functions
 */

import { createElement } from 'react'

import { BarChart3, Calendar, BookOpen, Scale } from 'lucide-react'

import { parseMoneyAmount } from '../../utils/formatters'

import type { TrialBalanceReport } from '../../services/reporting-service'

// ============================================================================
// Types
// ============================================================================

/** Subset of JournalEntry fields needed by the Dashboard. */
export interface DashboardEntry {
  id: string
  entryNumber: string
  entryDate: string
  memo: string
  totalDebit: { amount: number; currency: string }
}

export interface FinancialMetrics {
  totalAssets: number
  totalLiabilities: number
  totalEquity: number
  totalRevenue: number
  totalExpenses: number
  netIncome: number
}

// ============================================================================
// Constants
// ============================================================================

export const DASHBOARD_FEATURES = [
  {
    icon: createElement(BarChart3, { className: 'w-4 h-4' }),
    title: 'Overview Metrics',
    description: 'See total assets, liabilities, equity, and net income at a glance from your trial balance',
  },
  {
    icon: createElement(Calendar, { className: 'w-4 h-4' }),
    title: 'Fiscal Period Tracking',
    description: 'Monitor the current open fiscal period and quickly navigate to period management',
  },
  {
    icon: createElement(BookOpen, { className: 'w-4 h-4' }),
    title: 'Recent Entries',
    description: 'View the latest journal entries with quick access to create new transactions',
  },
  {
    icon: createElement(Scale, { className: 'w-4 h-4' }),
    title: 'Trial Balance Summary',
    description: 'Verify that your books are balanced with real-time debit and credit totals',
  },
]

export const DASHBOARD_STEPS = [
  {
    title: 'Set up fiscal periods',
    description: 'Configure your fiscal year and periods so journal entries can be posted to the correct timeframe',
  },
  {
    title: 'Create your chart of accounts',
    description: 'Define the account structure that organizes your assets, liabilities, equity, revenue, and expenses',
  },
  {
    title: 'Start recording transactions',
    description: 'Create journal entries to capture financial activity and watch your dashboard come to life',
  },
]

export const DASHBOARD_WHAT_IS_IT = {
  title: 'What is the Dashboard?',
  description: "The dashboard provides a high-level overview of your organization's financial health. It displays key metrics from your trial balance, tracks the current fiscal period, and gives quick access to recent journal entries and common actions.",
}

// ============================================================================
// Pure helpers
// ============================================================================

/** Format a date string to a short human-readable label (e.g. "Jan 5"). */
export function formatEntryDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
}

/** Derive CSS colour classes for the fiscal period icon based on current state. */
export function getPeriodIconColorClass(
  hasFiscalPeriods: boolean | null,
  hasCurrent: boolean,
): string {
  if (hasCurrent) {
    return 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400'
  }
  if (hasFiscalPeriods === false) {
    return 'bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-400'
  }
  return 'bg-surface-secondary text-foreground-secondary'
}

/** Compute financial metric totals from a trial balance report. */
export function computeFinancialMetrics(tb: TrialBalanceReport | null): FinancialMetrics {
  const zero: FinancialMetrics = {
    totalAssets: 0,
    totalLiabilities: 0,
    totalEquity: 0,
    totalRevenue: 0,
    totalExpenses: 0,
    netIncome: 0,
  }

  if (!tb?.rows) {
    return zero
  }

  const balances = tb.rows.reduce(
    (acc, r) => {
      const debit = parseMoneyAmount(r.debitBalance.amount)
      const credit = parseMoneyAmount(r.creditBalance.amount)
      const balance = debit - credit

      switch (r.accountType) {
        case 'asset':
          acc.totalAssets += balance
          break
        case 'liability':
          acc.totalLiabilities += -balance
          break
        case 'equity':
          acc.totalEquity += -balance
          break
        case 'revenue':
          acc.totalRevenue += -balance
          break
        case 'expense':
          acc.totalExpenses += balance
          break
      }
      return acc
    },
    {
      totalAssets: 0,
      totalLiabilities: 0,
      totalEquity: 0,
      totalRevenue: 0,
      totalExpenses: 0,
    },
  )

  return { ...balances, netIncome: balances.totalRevenue - balances.totalExpenses }
}
